import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { contactKeys } from '@/lib/queryKeys';
import { apiPost, operationsClient } from '@/lib/api';

interface SendResult {
  success: boolean;
  channel: string;
  to: string | null;
  error: string | null;
}

interface SendSmsVars {
  body: string;
  toNumber?: string;
  phoneNumberId?: string;
}

interface SendEmailVars {
  subject: string;
  body: string;
  toEmail?: string;
}

function resultToast(res: SendResult | undefined, channel: 'SMS' | 'Email') {
  if (res?.success) toast.success(`${channel} sent`);
  else toast.warning(`Logged — delivery skipped${res?.error ? `: ${res.error}` : ''}`);
}

export function useSendContactSms(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation<SendResult, SendSmsVars>({
    mutationFn: (dto) =>
      apiPost<SendResult, SendSmsVars>(
        operationsClient,
        `/contacts/${contactId}/communications/sms`,
        dto,
      ),
    onSuccess: (res) => {
      // The event is logged on the server even when delivery is skipped/fails,
      // so always refresh the feed; the result only drives the toast copy.
      void qc.invalidateQueries({ queryKey: contactKeys.communicationsBase(contactId) });
      resultToast(res, 'SMS');
    },
  });
}

export function useSendContactEmail(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation<SendResult, SendEmailVars>({
    mutationFn: (dto) =>
      apiPost<SendResult, SendEmailVars>(
        operationsClient,
        `/contacts/${contactId}/communications/email`,
        dto,
      ),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: contactKeys.communicationsBase(contactId) });
      resultToast(res, 'Email');
    },
  });
}
